import type { VenueType, PrimaryAtmosphere, Amenity } from '@wineryxplorer/shared';
import type { MapBounds } from '../../hooks/useSearchScope';
import { WishlistInBoundsChip } from '../WishlistInBoundsChip';
import { MobileMapButton } from './MobileMapButton';
import { SortControls } from './SortControls';
import type { SortOption } from './SortControls';

interface ResultsHeaderProps {
  isPending: boolean;
  total: number | undefined;
  isMapAreaMode: boolean;
  regionName: string | undefined;
  cityName: string | undefined;
  hasUserLocation: boolean;
  hasActiveFilters: boolean;
  searchText: string;
  venueTypes: VenueType[];
  atmospheres: PrimaryAtmosphere[];
  amenities: Amenity[];
  onClearFilters: () => void;
  mapBounds: MapBounds | null;
  onShowMobileMap: () => void;
  sortOption: SortOption;
  onSortChange: (sort: SortOption) => void;
}

/**
 * Result-count summary, active-filter breadcrumb, wishlist chip, mobile map
 * toggle, and sort control — the band between the filters and the results.
 */
export const ResultsHeader = ({
  isPending,
  total,
  isMapAreaMode,
  regionName,
  cityName,
  hasUserLocation,
  hasActiveFilters,
  searchText,
  venueTypes,
  atmospheres,
  amenities,
  onClearFilters,
  mapBounds,
  onShowMobileMap,
  sortOption,
  onSortChange,
}: ResultsHeaderProps) => (
  <div className="flex items-center justify-between flex-wrap gap-2 pt-2 border-t border-gray-100">
    <div className="text-sm text-gray-600">
      {isPending ? (
        'Searching...'
      ) : total !== undefined ? (
        <>
          <div>
            {total} winer{total === 1 ? 'y' : 'ies'}{' '}
            {isMapAreaMode
              ? 'in this area'
              : regionName
                ? `in ${regionName}`
                : cityName
                  ? `near ${cityName}`
                  : hasUserLocation
                    ? 'near you'
                    : 'matching your search'}
          </div>
          {hasActiveFilters && (
            <div className="text-xs text-gray-500 mt-0.5">
              Filtered by:{' '}
              {[
                regionName,
                cityName,
                searchText,
                ...venueTypes,
                ...atmospheres,
                ...amenities,
              ]
                .filter(Boolean)
                .join(' · ')}{' '}
              <button
                type="button"
                onClick={onClearFilters}
                className="text-primary-600 hover:text-primary-800 underline"
              >
                Clear all
              </button>
            </div>
          )}
        </>
      ) : null}
      <div className="mt-1">
        <WishlistInBoundsChip bounds={mapBounds} />
      </div>
    </div>
    <div className="flex items-center gap-2">
      <MobileMapButton onClick={onShowMobileMap} />
      <SortControls
        value={sortOption}
        onChange={onSortChange}
      />
    </div>
  </div>
);
